import classNames from 'classnames';
import { FC, FormEvent, useState } from 'react';

import { useOrders } from '../../../lib/api/gql/hooks/useOrders';
import { fetchRest, HttpMethod } from '../../../lib/api/rest/rest.client';
import { useCart } from '../../../lib/context/cart/CartContext';
import { useChat } from '../../../lib/context/chat/ChatContext';
import { useTrackEvent } from '../../../lib/tracking/useTrack';
import type { Meal } from '../../../lib/types/Meal.type';
import { MessageSenderEnum } from '../../../lib/types/Message.type';
import { Button } from '../../atoms/Button';
import { Textarea } from '../../atoms/Textarea';
import { SendIcon } from '../../atoms/icons/SendIcon';
import { WriteIcon } from '../../atoms/icons/WriteIcon';
import { MealsComponent } from '../MealsComponent/MealsComponent';

import styles from './UserInput.module.css';

export const UserInput: FC = () => {
    const { createOrder } = useOrders();
    const { getCartCount } = useCart();
    const chatContext = useChat();
    const cartQty = getCartCount();
    const trackEvent = useTrackEvent();
    const [inputValue, setInputValue] = useState('');

    if (!chatContext) {
        return null;
    }

    const { messages, sendMessage, conversationState, setLoader, isLoading, addComponentMessage, deliveryDate, plan } = chatContext;

    const lastMessage = messages[messages.length - 1];

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (!inputValue && inputValue.length === 0) {
            return;
        }

        if (conversationState === 'mealsRecommendations') {
            setLoader(true);
            fetchRest({
                url: process.env.REACT_APP_MEAL_RECOMMENDER_URL + '/api/v1/chat',
                method: HttpMethod.POST,
                body: {
                    message: inputValue,
                    date: deliveryDate
                }
            }).then((response) => {
                const message = response?.answer ?? 'Something went wrong, please try again.';
                if (response?.meals?.length > 0) {
                    addComponentMessage({
                        content: response?.answer,
                        component: <MealsComponent data={response.meals as Meal[]} />,
                        interaction: 'meal-recommender'
                    });
                } else {
                    sendMessage({ type: MessageSenderEnum.ai, content: message });
                }

                trackEvent('ai_response', {
                    value: inputValue,
                    response: message,
                    // Set global value from context.
                    prevMessage: lastMessage,
                    state: conversationState,
                });
            }).catch((error) => {
                sendMessage({ type: MessageSenderEnum.ai, content: 'Something went wrong, please try again.' });
                console.log('error', error)
                // trackEvent('ai_response_error', {
                //     value: inputValue,
                //     response: 'error',
                //     // Set global value from context.
                //     prevMessage: lastMessage,
                //     state: conversationState,
                // });
            }).finally(() => {
                setLoader(false);
            })
        }


        trackEvent('user_input', {
            action: 'user_input',
            value: inputValue,
            // Set global value from context.
            prevMessage: lastMessage,
            state: conversationState,
        });

        sendMessage({ type: MessageSenderEnum.human, content: inputValue });
        setInputValue('');
    };

    const handleConfirm = () => {
        createOrder();
    }

    return (
        <div className={
            classNames(
                styles.userInput,
                !isLoading && styles.userInputField
            )
        }>
            {
                plan && cartQty >= plan?.mealsPerDelivery && (
                    <div>
                        <Button className={styles.confirm} onClick={handleConfirm}>Confirm</Button>
                    </div>
                )
            }
            {!isLoading && (
                <form onSubmit={handleSubmit}>
                    <div className={styles.writing}>
                        <div className={styles.writeIcon}>
                            <WriteIcon />
                        </div>
                        <Textarea
                            value={inputValue}
                            placeholder="Message"
                            onChange={(e) => setInputValue(e.target.value)}
                            onEnterPress={handleSubmit as any}
                        />
                    </div>
                    <Button
                        type='submit'
                        className={
                            classNames([
                                styles.send,
                                inputValue.length > 0 && styles.sendEnabled
                            ])
                        }
                    >
                        <SendIcon />
                    </Button>
                </form>
            )}
        </div>
    );
};
